import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/FAQ.css';
import updateDocumentTitle from '../utils/updateDocumentTitle';

const FAQ = () => {
  const [openQuestion, setOpenQuestion] = useState(null);

  updateDocumentTitle('FAQ');

  const toggleQuestion = (index) => {
    if (openQuestion === index) {
      setOpenQuestion(null);
    } else {
      setOpenQuestion(index);
    }
  };

  // FAQ sections with categories
  const aboutContractWell = [
    {
      question: "What is ContractWell?",
      answer: "ContractWell offers lawyer-grade, customizable legal templates so you can protect your business without steep hourly fees. Each contract is editable, easy to understand, and crafted by experienced legal professionals. We don't provide legal advice. See our disclaimer for details."
    },
    {
      question: "What makes ContractWell different?",
      answer: "Most free templates are too generic to truly protect you. Ours are legally thorough, customizable, and built for real-world business needs, so you can protect your work without starting from scratch."
    }
  ];

  const usingTemplates = [
    {
      question: "How do I access my contract after purchase?",
      answer: "You'll get instant access to download your template in Microsoft Word or Google Docs format, ready to edit right away."
    },
    {
      question: "Can I use my contract multiple times?",
      answer: "Yes. Your purchase is a one-time fee for ongoing use—no subscriptions or renewals."
    },
    {
      question: "Can I edit my contract multiple times?",
      answer: "Absolutely. Edit and reuse your template as often as you need—it's built to save you time, money, and legal fees."
    },
    {
      question: "Can I collaborate on contracts with others?",
      answer: "Yes. Share your template in Word or Google Docs with co-founders, clients, or legal advisors."
    }
  ];

  const whatsIncluded = [
    {
      question: "What contracts do you offer?",
      answer: "Our growing library includes:\n- Non-Disclosure Agreements (Mutual & One-Way)\n- Website Terms & Conditions\n- Privacy Policies\n- Coaching & Influencer Agreements\n- Master Service Agreements (MSAs)\n\nDon't see what you need? Let us know! We're always adding new templates."
    },
    {
      question: "What's included with each contract?",
      answer: "Every template comes with a plain-language Cheat Sheet explaining key sections in clear, simple terms—so you know exactly what you're signing."
    },
    {
      question: "Do I need a lawyer to use these templates?",
      answer: "No. They're designed for you to use on your own. But if you want to be sure they meet local legal requirements, you can have a lawyer review your final draft."
    }
  ];

  const otherQuestions = [
    {
      question: "How often are templates updated?",
      answer: "We add new templates monthly and regularly review existing ones to keep them current."
    },
    {
      question: "What if I don't see the contract I need?",
      answer: "We welcome suggestions. Tell us what you're looking for and we may add it to our library."
    },
    {
      question: "What about \"Governing Law\"?",
      answer: "Our contracts work across the U.S. and Canada. Jurisdiction is left blank so you can:\n- Choose your local city/province/state if you operate in one place\n- Pick a neutral location for multi-region agreements\n- Consult a lawyer if you're unsure"
    },
    {
      question: "What's your refund policy?",
      answer: "Because our products are digital downloads, all sales are final. Please review descriptions carefully before buying. Questions? Contact contract.well@contractwell.ca."
    },
    {
      question: "Can I preview contracts before buying?",
      answer: "We don't offer full previews, but we provide detailed descriptions of each template. If you're unsure, email support@contractwell.com. We're happy to help you choose the right one."
    }
  ];

  // Combine all sections into one array for compatibility with existing component structure
  const faqData = [
    ...aboutContractWell,
    ...usingTemplates,
    ...whatsIncluded,
    ...otherQuestions
  ];
  

  return (
    <div className="faq-page">
      <div className="faq-title-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <p className="subtitle">Find quick answers to common questions about ContractWell's lawyer-grade contract templates.</p>
        </div>
        <div className="faq-image-top">
          <img src="/images/qmark.png" alt="Question Mark with Person" />
        </div>
      </div>
      
      <div className="shop-button-container">
        <Link to="/library">
          <button className="shop-contracts-btn">Shop Contracts</button>
        </Link>
      </div>
      
      <div className="faq-container">
        <div className="faq-content">
          <div className="faq-questions">
            <div className="faq-section">
              <h2 className="faq-section-title">About ContractWell</h2>
              {aboutContractWell.map((faq, index) => (
                <div key={`about-${index}`} className="faq-item">
                  <div 
                    className="faq-question" 
                    onClick={() => toggleQuestion(`about-${index}`)}
                  >
                    <span className="faq-icon">{openQuestion === `about-${index}` ? '−' : '+'}</span>
                    <h3>{faq.question}</h3>
                  </div>
                  {openQuestion === `about-${index}` && (
                    <div className="faq-answer">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="faq-section">
              <h2 className="faq-section-title">Using Your Templates</h2>
              {usingTemplates.map((faq, index) => (
                <div key={`using-${index}`} className="faq-item">
                  <div 
                    className="faq-question" 
                    onClick={() => toggleQuestion(`using-${index}`)}
                  >
                    <span className="faq-icon">{openQuestion === `using-${index}` ? '−' : '+'}</span>
                    <h3>{faq.question}</h3>
                  </div>
                  {openQuestion === `using-${index}` && (
                    <div className="faq-answer">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="faq-section">
              <h2 className="faq-section-title">What's Included</h2>
              {whatsIncluded.map((faq, index) => (
                <div key={`included-${index}`} className="faq-item">
                  <div 
                    className="faq-question" 
                    onClick={() => toggleQuestion(`included-${index}`)}
                  >
                    <span className="faq-icon">{openQuestion === `included-${index}` ? '−' : '+'}</span>
                    <h3>{faq.question}</h3>
                  </div>
                  {openQuestion === `included-${index}` && (
                    <div className="faq-answer">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="faq-section">
              <h2 className="faq-section-title">Other Questions</h2>
              {otherQuestions.map((faq, index) => (
                <div key={`other-${index}`} className="faq-item">
                  <div 
                    className="faq-question" 
                    onClick={() => toggleQuestion(`other-${index}`)}
                  >
                    <span className="faq-icon">{openQuestion === `other-${index}` ? '−' : '+'}</span>
                    <h3>{faq.question}</h3>
                  </div>
                  {openQuestion === `other-${index}` && (
                    <div className="faq-answer">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className="disclaimer-section">
        <div className="disclaimer-circle">
          <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#053C26" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="#053C26" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="disclaimer-content">
          <div className="disclaimer-text">
            <p>ContractWell is not a law firm and does not offer legal services or legal advice. The resources provided on this website — including templates, guides and cheat sheet are intended for general informational purposes only and should not be considered a substitute for professional legal counsel.
Purchasing, downloading, or using any material from ContractWell does not establish an attorney-client or solicitor-client relationship between you and ContractWell or any of its contributors. As each business and legal context is different, we recommend consulting a qualified lawyer in your jurisdiction if you require personalized legal advice for specific issues.
Please refer to our complete Disclaimer for more information.</p>
            <h2>DISCLAIMER</h2>
          </div>
        </div>
      </div>
      
      <section className="signup">
        <div className="signup-content">
          <h2>Join Our Mailing List</h2>
          <form className="signup-form">
            <input
              type="email"
              className="email-input"
              placeholder="Your email address"
              required
            />
            <button type="submit" className="signup-btn">
              Sign me up
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default FAQ; 